import { useMemo } from 'react';

import { Separator } from './Separator';
import { Label } from './label';
import { cn } from './utils/cn';

// ─── FieldSet ──────────────────────────────────────────────────────────

function FieldSet({ className, ...props }: React.ComponentProps<'fieldset'>) {
  return (
    <fieldset
      data-slot="field-set"
      className={cn(
        'flex flex-col gap-6',
        'has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3',
        className,
      )}
      {...props}
    />
  );
}

// ─── FieldLegend ───────────────────────────────────────────────────────

function FieldLegend({
  className,
  size = 'default',
  ...props
}: React.ComponentProps<'legend'> & { size?: 'default' | 'sm' }) {
  return (
    <legend
      data-slot="field-legend"
      className={cn(
        'mb-3 font-medium',
        size === 'default' ? 'text-lg' : 'text-md',
        className,
      )}
      {...props}
    />
  );
}

// ─── FieldGroup ────────────────────────────────────────────────────────

function FieldGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-group"
      className={cn(
        'group/field-group @container/field-group flex w-full flex-col gap-7 data-[slot=checkbox-group]:gap-3 *:data-[slot=field-group]:gap-4',
        // Card variant: gray bg, rounded, row padding, borders between items
        'data-[variant=card]:gap-0 data-[variant=card]:rounded-lg data-[variant=card]:bg-bg-secondary',
        'data-[variant=card]:*:data-[slot=field]:p-5',
        'data-[variant=card]:[&>[data-slot=field]:has([data-slot=field-content])]:py-7',
        'data-[variant=card]:[&>[data-slot=field]+[data-slot=field]]:border-t data-[variant=card]:[&>[data-slot=field]+[data-slot=field]]:border-border-primary',
        // Card variant + horizontal: constrain the control to max-w-[250px] so the label can fill the rest of the row
        'data-[variant=card]:[&>[data-slot=field][data-orientation=horizontal]>[data-slot=input]]:max-w-[250px]',
        'data-[variant=card]:[&>[data-slot=field][data-orientation=horizontal]>[data-slot=textarea]]:max-w-[250px]',
        'data-[variant=card]:[&>[data-slot=field][data-orientation=horizontal]>[data-slot=select-trigger]]:max-w-[250px]',
        // List variant: no gap, padded rows, borders between items
        'data-[variant=list]:gap-0',
        'data-[variant=list]:*:data-[slot=field]:px-5 data-[variant=list]:*:data-[slot=field]:py-7',
        'data-[variant=list]:[&>[data-slot=field]+[data-slot=field]]:border-t data-[variant=list]:[&>[data-slot=field]+[data-slot=field]]:border-border-primary',
        // List variant: handle button wrappers (navigation rows)
        'data-[variant=list]:[&>button+button]:border-t data-[variant=list]:[&>button+button]:border-border-primary',
        'data-[variant=list]:[&>button>[data-slot=field]]:px-5 data-[variant=list]:[&>button>[data-slot=field]]:py-7',
        className,
      )}
      {...props}
    />
  );
}

// ─── Field ─────────────────────────────────────────────────────────────

type FieldOrientation = 'vertical' | 'horizontal' | 'responsive';

const orientationStyles: Record<FieldOrientation, string> = {
  vertical: 'flex-col [&>*]:w-full [&>.sr-only]:w-auto',
  horizontal: [
    'flex-row items-center',
    '[&>[data-slot=field-label]]:flex-auto',
    // Only top-align for checkbox/radio (they sit beside multi-line text)
    'has-[>[role=checkbox]]:items-start has-[>[role=radio]]:items-start',
  ].join(' '),
  responsive: [
    'flex-col @md/field-group:flex-row @md/field-group:items-center [&>*]:w-full @md/field-group:[&>*]:w-auto [&>.sr-only]:w-auto',
    '@md/field-group:[&>[data-slot=field-label]]:flex-auto',
    // Only top-align for checkbox/radio
    '@md/field-group:has-[>[role=checkbox]]:items-start @md/field-group:has-[>[role=radio]]:items-start',
  ].join(' '),
};

/**
 * Layout wrapper that pairs a form control with its label, description, and
 * error message. Controls orientation (vertical, horizontal, responsive) and
 * propagates disabled/invalid state to children via data attributes.
 *
 * @example
 * <Field orientation="horizontal">
 *   <Checkbox id="terms" />
 *   <FieldContent>
 *     <FieldLabel htmlFor="terms">Accept terms</FieldLabel>
 *     <FieldDescription>Required to continue.</FieldDescription>
 *   </FieldContent>
 * </Field>
 */
function Field({
  className,
  orientation = 'vertical',
  ...props
}: React.ComponentProps<'div'> & { orientation?: FieldOrientation }) {
  return (
    <div
      role="group"
      data-slot="field"
      data-orientation={orientation}
      className={cn(
        'group/field flex w-full gap-2 data-[invalid=true]:text-destructive',
        // Radio/checkbox items need 16px gap between circle and text (Figma spacing/4)
        'has-[>[role=checkbox]]:gap-4 has-[>[role=radio]]:gap-4',
        // Nudge control down 2px so it aligns with the text baseline
        '*:[[role=checkbox]]:mt-0.5 *:[[role=radio]]:mt-0.5',
        orientationStyles[orientation],
        className,
      )}
      {...props}
    />
  );
}

// ─── FieldContent ──────────────────────────────────────────────────────

function FieldContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-content"
      className={cn(
        'group/field-content flex flex-1 flex-col gap-0.5 leading-snug',
        className,
      )}
      {...props}
    />
  );
}

// ─── FieldLabel ────────────────────────────────────────────────────────

function FieldLabel({
  className,
  size = 'default',
  ...props
}: React.ComponentProps<typeof Label> & { size?: 'default' | 'sm' }) {
  return (
    <Label
      data-slot="field-label"
      className={cn(
        'group/field-label peer/field-label flex w-fit gap-2 leading-snug group-data-[disabled=true]/field:opacity-50',
        'has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col has-[>[data-slot=field]]:rounded-md has-[>[data-slot=field]]:border *:data-[slot=field]:p-4',
        'has-data-[state=checked]:border-primary has-data-[state=checked]:bg-primary/5',
        size === 'sm' &&
          'text-2xs leading-[1.42] font-bold tracking-[0.55px] text-text-tertiary uppercase',
        className,
      )}
      {...props}
    />
  );
}

// ─── FieldTitle ────────────────────────────────────────────────────────

function FieldTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-label"
      className={cn(
        'flex w-fit items-center gap-2 text-sm leading-snug font-medium group-data-[disabled=true]/field:opacity-50',
        className,
      )}
      {...props}
    />
  );
}

// ─── FieldDescription ──────────────────────────────────────────────────

function FieldDescription({
  className,
  size = 'default',
  ...props
}: React.ComponentProps<'p'> & { size?: 'default' | 'md' }) {
  return (
    <p
      data-slot="field-description"
      className={cn(
        'font-medium text-text-tertiary group-has-data-[orientation=horizontal]/field:text-balance',
        size === 'md' ? 'text-sm' : 'text-xs',
        '[[data-slot=field-legend]+&]:-mt-1.5',
        '[&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary',
        className,
      )}
      {...props}
    />
  );
}

// ─── FieldSeparator ────────────────────────────────────────────────────

function FieldSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<'div'> & {
  children?: React.ReactNode;
}) {
  return (
    <div
      data-slot="field-separator"
      data-content={Boolean(children)}
      className={cn(
        'relative -my-2 h-5 text-sm group-data-[variant=outline]/field-group:-mb-2',
        className,
      )}
      {...props}
    >
      <Separator className="absolute inset-0 top-1/2" />
      {children && (
        <span
          className="relative mx-auto block w-fit bg-bg-primary px-2 text-text-tertiary"
          data-slot="field-separator-content"
        >
          {children}
        </span>
      )}
    </div>
  );
}

// ─── FieldError ────────────────────────────────────────────────────────

function FieldError({
  className,
  children,
  errors,
  ...props
}: React.ComponentProps<'div'> & {
  errors?: Array<{ message?: string } | undefined>;
}) {
  const content = useMemo(() => {
    if (children) {
      return children;
    }

    if (!errors?.length) {
      return null;
    }

    const uniqueErrors = [
      ...new Map(errors.map((error) => [error?.message, error])).values(),
    ];

    if (uniqueErrors?.length === 1) {
      return uniqueErrors[0]?.message;
    }

    return (
      <ul className="ml-4 flex list-disc flex-col gap-1">
        {uniqueErrors.map(
          (error, index) =>
            error?.message && <li key={index}>{error.message}</li>,
        )}
      </ul>
    );
  }, [children, errors]);

  if (!content) {
    return null;
  }

  return (
    <div
      role="alert"
      data-slot="field-error"
      className={cn('text-xs font-medium text-destructive', className)}
      {...props}
    >
      {content}
    </div>
  );
}

export {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldTitle,
};
